import React from "react";
import { Flex, Button } from "@chakra-ui/react";
import { ff } from "theme";
import type { CancelIntentAction, CancelIntentOption } from "@foodfluence/shared";

/**
 * Chips shown under the inbox cancel agent's prompt.
 *
 * The set is decided server-side per collab and arrives on the message, so this renders whatever
 * it is given: Reschedule + Cancel + Keep, Cancel + Keep (retail, or nothing bookable), or
 * Reschedule + Keep (inside the cancel cutoff, where moving the visit still works).
 *
 * Deliberately NOT CheckInChips: those carry an auto-cancel countdown and post to the check-in
 * response endpoint. Here the destructive choice always has the lightest styling and never holds
 * the primary slot — the creator is one tap from ending a funded collab. Cancel additionally
 * opens a confirmation modal on the caller's side, since this prompt is now a choice between
 * actions rather than a yes/no question that could stand as the confirmation itself.
 *
 * There is no countdown: nothing expires visibly, and a ticking clock next to a cancel button
 * would read as pressure to cancel.
 */

interface CancelIntentChipsProps {
  options: CancelIntentOption[];
  /** msgKey currently submitting (null when idle). */
  submittingKey: string | null;
  /** Which action is submitting, so only that chip shows a spinner. */
  submittingAction: CancelIntentAction | null;
  msgKey: string;
  onChip: (action: CancelIntentAction, label: string, msgKey: string) => void;
}

const CancelIntentChips: React.FC<CancelIntentChipsProps> = ({
  options,
  submittingKey,
  submittingAction,
  msgKey,
  onChip,
}) => {
  const rescheduleOption = options.find((o) => o.action === "rescheduleInstead");
  const keepOption = options.find((o) => o.action === "keepCollab");
  const cancelOption = options.find((o) => o.action === "confirmCancel");
  const undoOption = options.find((o) => o.action === "undoCancel");

  const isBusy = submittingKey === msgKey;

  // Undo is its own shape: a single quiet button on the "I've canceled this" message, not one of a
  // pair. Styled plainly rather than as a warning — putting the collab back is the safe action, and
  // the alarming styling belongs on the tap that ended it.
  if (undoOption) {
    return (
      <Flex gap="8px" wrap="wrap" mt="10px" maxW="100%">
        <Button
          onClick={() => onChip("undoCancel", undoOption.label, msgKey)}
          isLoading={isBusy}
          loadingText="Undoing"
          h="44px"
          px="18px"
          minW="0"
          borderRadius="full"
          bg="white"
          color={ff.ink}
          border="1px solid"
          borderColor={ff.border}
          fontFamily="display"
          fontSize="14px"
          fontWeight={600}
          _hover={{ bg: ff.grey }}
          _active={{ bg: ff.line }}
          sx={{ WebkitTapHighlightColor: "transparent" }}
        >
          {undoOption.label}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex gap="8px" wrap="wrap" mt="10px" maxW="100%">
      {/* Primary when present: moving the visit keeps the collab alive where cancelling ends it,
          so it is the option the thumb lands on. When there is no reschedule to offer, "keep it"
          takes the primary slot instead — the safe answer is never the lightest one. */}
      {rescheduleOption && (
        <Button
          onClick={() => onChip("rescheduleInstead", rescheduleOption.label, msgKey)}
          isDisabled={isBusy && submittingAction !== "rescheduleInstead"}
          isLoading={isBusy && submittingAction === "rescheduleInstead"}
          h="44px"
          px="18px"
          minW="0"
          borderRadius="full"
          bg={ff.ink}
          color="white"
          fontFamily="display"
          fontSize="15px"
          fontWeight={600}
          _hover={{ bg: ff.inkSoft }}
          _active={{ bg: ff.inkSoft }}
          sx={{ WebkitTapHighlightColor: "transparent" }}
        >
          {rescheduleOption.label}
        </Button>
      )}

      {keepOption && (
        <Button
          onClick={() => onChip("keepCollab", keepOption.label, msgKey)}
          isDisabled={isBusy && submittingAction !== "keepCollab"}
          isLoading={isBusy && submittingAction === "keepCollab"}
          h="44px"
          px="18px"
          minW="0"
          borderRadius="full"
          bg={rescheduleOption ? "white" : ff.ink}
          color={rescheduleOption ? ff.ink : "white"}
          border={rescheduleOption ? "1px solid" : undefined}
          borderColor={rescheduleOption ? ff.border : undefined}
          fontFamily="display"
          fontSize="15px"
          fontWeight={rescheduleOption ? 700 : 800}
          _hover={{ bg: rescheduleOption ? ff.grey : ff.inkSoft }}
          _active={{ bg: rescheduleOption ? ff.line : ff.inkSoft }}
          sx={{ WebkitTapHighlightColor: "transparent" }}
        >
          {keepOption.label}
        </Button>
      )}

      {cancelOption && (
        <Button
          onClick={() => onChip("confirmCancel", cancelOption.label, msgKey)}
          isDisabled={isBusy && submittingAction !== "confirmCancel"}
          isLoading={isBusy && submittingAction === "confirmCancel"}
          loadingText="Canceling"
          h="44px"
          px="16px"
          minW="0"
          borderRadius="full"
          bg="white"
          color={ff.danger}
          border="1px solid"
          borderColor={ff.border}
          fontFamily="display"
          fontSize="14px"
          fontWeight={600}
          _hover={{ bg: ff.grey }}
          _active={{ bg: ff.line }}
          sx={{ WebkitTapHighlightColor: "transparent" }}
        >
          {cancelOption.label}
        </Button>
      )}
    </Flex>
  );
};

export default CancelIntentChips;
